// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Customise Home (design/home-mobile-compact round 2, r2-set-*). How Home is
// laid out on this device: layout and density as big preview cards, the sections and their
// order, and what a swipe on a Home row does. Everything reads and writes HomePreferences,
// so Home changes the moment a setting does. On a wide desktop sheet the sections and the
// swipe settings sit side by side.

import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import styles from '@app/features/konvo_today/components/settings/CustomiseHome.module.css';
import {HomeLayoutSettings} from '@app/features/konvo_today/components/settings/HomeLayoutSettings';
import {HomeSectionsSettings} from '@app/features/konvo_today/components/settings/HomeSectionsSettings';
import {HomeSendingSettings} from '@app/features/konvo_today/components/settings/HomeSendingSettings';
import {HomeSwipeSettings} from '@app/features/konvo_today/components/settings/HomeSwipeSettings';
import HomePreferences, {
	HOME_SECTION_IDS,
	HOME_UNDO_SEND_DEFAULT_SECONDS,
} from '@app/features/konvo_today/state/HomePreferences';
import {ArrowClockwiseIcon} from '@app/features/ui/icons/Icons';
import {ActionButton} from '@app/features/ui/konvo_controls/KonvoControls';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const RESET_DESCRIPTOR = msg({
	message: 'Reset to defaults',
	comment: 'Customise Home: button that puts every Home setting on this device back to how it started.',
});

/** True while every Home setting is still at its starting value (Reset has nothing to do). */
export function isHomeAtDefaults(prefs: typeof HomePreferences = HomePreferences): boolean {
	const sections = prefs.orderedSections;
	return (
		prefs.effectiveLayout === 'stacks' &&
		prefs.density === 'comfortable' &&
		sections.every((section, index) => section.visible && section.id === HOME_SECTION_IDS[index]) &&
		prefs.rowLimit === 3 &&
		prefs.suggestionStyle === 'stack' &&
		prefs.deckThreshold === 0 &&
		prefs.undoSendMs === HOME_UNDO_SEND_DEFAULT_SECONDS * 1000 &&
		prefs.swipeAction('right', 'short') === 'done' &&
		prefs.swipeAction('right', 'long') === 'reply' &&
		prefs.swipeAction('left', 'short') === 'later' &&
		prefs.swipeAction('left', 'long') === 'dismiss' &&
		prefs.trayEnabled &&
		prefs.distance === 'medium' &&
		prefs.haptics &&
		prefs.effectiveOpenConversations === 'popup' &&
		prefs.sourceRail &&
		prefs.collapsed.length === 0 &&
		prefs.effectiveAllChatsGrouping === 'day' &&
		prefs.collapsedChatGroups.length === 0
	);
}

export const CustomiseHomeSettingsTab = observer(() => {
	const {i18n} = useLingui();
	return (
		<SettingsTabContainer>
			<SettingsTabContent className={styles.page}>
				<HomeLayoutSettings />
				<div className={styles.columns}>
					<div className={styles.column}>
						<HomeSectionsSettings />
					</div>
					<div className={styles.column}>
						<HomeSendingSettings />
						<HomeSwipeSettings />
					</div>
				</div>
				<div className={styles.resetRow}>
					<ActionButton
						small
						tone="secondary"
						icon={<ArrowClockwiseIcon size={16} />}
						disabled={isHomeAtDefaults()}
						onClick={HomePreferences.reset}
						data-flx="konvo-today.customise-home.reset"
					>
						{i18n._(RESET_DESCRIPTOR)}
					</ActionButton>
				</div>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default CustomiseHomeSettingsTab;
